import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import { HiOutlineCloudArrowDown } from 'react-icons/hi2';
import { Fragment, useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import './ia-summary-modal.css';

export const renderTextStructure = (text) => {
  return text
    ?.substring(1)
    .substring(0, text.length -2)
    .split('\\n')
    .map(s => s?.trim());
};

const findIndexOf = (target, array) => {
  const regex = new RegExp(target, 'i');
  return array.findIndex(item => regex.test(String(item)));
};

// El prompt rotula el perfil predominante de una sola forma, pero los resúmenes ya
// guardados en base traen indistintamente "Perfil Principal" o "Perfil predominante",
// así que el parseo acepta las dos.
const PRINCIPAL_PROFILE_LABEL = 'Perfil (Principal|Predominante):';
const SECONDARY_PROFILE_LABEL = 'Perfil Secundario:';

/**
 * Devuelve lo que viene después del primer `:` de la línea. Partir por todos los `:`
 * truncaba cualquier valor que llevara uno dentro (ej. "PATRÓN DISC: INFLUYENTE",
 * que quedaba reducido a "PATRÓN DISC").
 */
const valueAfterLabel = (line) => {
  const separator = String(line).indexOf(':');
  return separator < 0 ? '' : String(line).slice(separator + 1).trim();
};

// Se ancla a las etiquetas concretas y no a `^Perfil\s`: una descripción puede
// empezar legítimamente con esa palabra ("Perfil orientado a las personas…") y
// descartarla haría desaparecer el texto sin ninguna señal en el reporte.
const SECTION_START = /^(Perfil\s+(Principal|Predominante|Secundario)|Estilo Adaptado DISC|Evaluación de Competencias|Datos del Evaluado)/i;

/**
 * Primera línea con contenido después de `index`: GPT suele dejar una línea en blanco
 * entre el perfil y su descripción, tal como el EJEMPLO del prompt. Se descarta si
 * resulta ser el encabezado de otra sección, porque en ese caso el perfil vino sin
 * descripción y estaríamos mostrando la sección siguiente en su lugar.
 */
const descriptionAfter = (array, index) => {
  const line = array.slice(index + 1).find(item => !!String(item).trim());
  return line && !SECTION_START.test(line) ? line : undefined;
};

/**
 * El prompt le pide a GPT rotular cada competencia como "Nombre (Nivel)", así que el
 * nivel entre paréntesis es lo que delimita un bloque del siguiente. Antes se
 * emparejaba de a dos líneas a ciegas: una sola descripción partida en dos dejaba
 * todas las competencias posteriores corridas, sin ninguna señal visible en el
 * reporte.
 */
// No se listan los cuatro niveles esperados: cualquier paréntesis de cierre al final
// de la línea marca un título. Si el modelo emite un nivel fuera del vocabulario
// ("Medio"), la competencia igual se reconoce en vez de fundirse con la anterior. Las
// descripciones terminan en punto, así que no colisionan con este patrón.
const COMPETENCY_TITLE = /\([^)]+\)\s*$/;

const buildCompetencies = (lines) => {
  // La de competencias es la última sección del formato, pero si apareciera otra
  // conocida después se corta acá: de lo contrario su encabezado y su texto terminan
  // concatenados dentro de la descripción de la última competencia. Una sección que
  // no esté en SECTION_START sigue quedando absorbida.
  const boundary = lines.findIndex(line => SECTION_START.test(line));
  const ownLines = boundary === -1 ? lines : lines.slice(0, boundary);

  // Si el modelo se saltó el nivel entre paréntesis no hay nada que delimite los
  // bloques, así que se cae al emparejado por pares para no perder el contenido.
  if (!ownLines.some(line => COMPETENCY_TITLE.test(line))) {
    return ownLines.reduce((competencies, line, index) => {
      if (index % 2 === 0) {
        competencies.push({ title: line, description: ownLines[index + 1] });
      }
      return competencies;
    }, []);
  }

  return ownLines.reduce((competencies, line) => {
    const current = competencies[competencies.length - 1];
    if (!current || COMPETENCY_TITLE.test(line)) {
      competencies.push({ title: line, description: '' });
    } else {
      current.description = current.description ? `${current.description} ${line}` : line;
    }
    return competencies;
  }, []);
};

export const extractDataFromString = (textData) => {
  const array = renderTextStructure(textData);
  if (!array) return null;
  const data = {};
  let indexTarget = findIndexOf('Nombre:', array);
  if (indexTarget >= 0) {
    data.name = valueAfterLabel(array[indexTarget]);
  }
  indexTarget = findIndexOf('Fecha de evaluación:', array);
  if (indexTarget >= 0) {
    data.date = valueAfterLabel(array[indexTarget]);
  }
  indexTarget = findIndexOf('Correo:', array);
  if (indexTarget >= 0) {
    data.email = valueAfterLabel(array[indexTarget]);
  }
  indexTarget = findIndexOf(PRINCIPAL_PROFILE_LABEL, array);
  if (indexTarget >= 0) {
    data.principalProfile = valueAfterLabel(array[indexTarget]);
    data.principalProfileDesciption = descriptionAfter(array, indexTarget);
  }
  indexTarget = findIndexOf(SECONDARY_PROFILE_LABEL, array);
  if (indexTarget >= 0) {
    data.secondaryProfile = valueAfterLabel(array[indexTarget]);
    data.secondaryProfileDesciption = descriptionAfter(array, indexTarget);
  }
  indexTarget = findIndexOf('Evaluación de Competencias', array);
  if (indexTarget >= 0) {
    data.competencies = buildCompetencies(array.slice(indexTarget + 1).filter(i => !!i));
  }
  return data;
};

const IaSummaryModal = (props) => {
  const { open, toggle, iaSummary } = props;
  const [textData, setTextData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const renderContent = () => {
    if (!textData) {
      return null;
    }
    const data = extractDataFromString(textData);

    return <>
      <p className='ia-modal-title'>Datos del Evaluado</p>
      <p><strong>Nombre:</strong> {data.name}</p>
      <p><strong>Fecha de evaluación:</strong> {data.date}</p>
      <p><strong>Correo:</strong> {data.email}</p>
      <p className='ia-modal-title'>Estilo Adaptado DISC</p>
      <p><strong>Perfil predominante: {data.principalProfile}</strong></p>
      {/* Un perfil puro (mismo acrónimo dominante y secundario) no trae perfil
          secundario: la etiqueta se omite en vez de quedar colgando vacía. */}
      {data.secondaryProfile && <p><strong>Perfil secundario: {data.secondaryProfile}</strong></p>}
      {/* El encabezado se omite si no hay ninguna descripción, para no dejarlo solo. */}
      {(data.principalProfileDesciption || data.secondaryProfileDesciption) &&
        <p><strong>Integración de estilos</strong></p>}
      {data.principalProfileDesciption && <p>{data.principalProfileDesciption}</p>}
      {data.secondaryProfileDesciption && <p>{data.secondaryProfileDesciption}</p>}
      <p className='ia-modal-title'>Evaluación de Competencias</p>
      {
        data.competencies?.map((item) => (
          <Fragment key={item.title}>
            <p><strong>{item.title}</strong></p>
            <p>{item.description}</p>
          </Fragment>
        ))
      }
    </>;
  };

  useEffect(() => {
    if (iaSummary) {
      if (iaSummary === "Cargando resumen...") {
        setLoading(true);
        setError(false);
        setTextData(null);
      } else if (iaSummary === "Ocurrió un problema al obtener el resumen. Intenten más tarde.") {
        setLoading(false);
        setError(true);
        setTextData(null);
      } else {
        setLoading(false);
        setError(false);
        setTextData(iaSummary);
      }
    }
  }, [iaSummary]);

  const handleCopy = () => {
    if (textData) {
      const array = renderTextStructure(textData);
      navigator.clipboard.writeText(array.filter(i => !!i).join('\n'))
        .then(() => {
          console.log('Resumen copiado al portapapeles');
        })
        .catch(err => {
          console.error('Error al copiar el resumen: ', err);
        });
    }
  };

  const handleDownload = async () => {
    if (!textData) return;
    
    setGeneratingPdf(true);
    
    try {
      // Obtener el elemento del contenedor del resumen
      const element = document.querySelector('.gm-ia-summary-container');
      if (!element) {
        console.error('No se encontró el contenedor del resumen');
        return;
      }

      // Guardar el box-shadow y border originales y removerlos temporalmente
      const originalBoxShadow = element.style.boxShadow;
      const originalBorder = element.style.border;
      element.style.boxShadow = 'none';
      element.style.border = 'none';

      // Capturar el elemento como imagen con html2canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Mejor calidad
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        removeContainer: true,
        imageTimeout: 0,
        allowTaint: true
      });

      // Restaurar el box-shadow y border originales
      element.style.boxShadow = originalBoxShadow;
      element.style.border = originalBorder;

      // Crear el PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      
      // Definir márgenes para el PDF (en mm)
      const margin = 20;
      
      // Calcular dimensiones disponibles considerando los márgenes
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2);
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calcular el ratio para mantener proporciones dentro del área disponible
      const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
      const width = imgWidth * ratio;
      const height = imgHeight * ratio;
      
      // Centrar la imagen en la página con márgenes
      const x = margin + (availableWidth - width) / 2;
      const y = margin + (availableHeight - height) / 2;

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', x, y, width, height);

      // Generar nombre del archivo
      const data = extractDataFromString(textData);
      const fileName = data?.name 
        ? `Resumen_GrowMe_IA_${data.name.replace(/\s+/g, '_')}.pdf`
        : 'Resumen_GrowMe_IA.pdf';

      // Descargar el PDF
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      alert('Ocurrió un error al generar el PDF. Por favor, intenta nuevamente.');
      
      // En caso de error, también restaurar el box-shadow y border
      const element = document.querySelector('.gm-ia-summary-container');
      if (element) {
        element.style.boxShadow = '';
        element.style.border = '';
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={toggle} size='lg' className="gm-ia-summary-modal">
      <div className='gm-modal-header'>
        <h2>
          Resumen GrowMe IA
          <span className="sparkle-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <g clipPath="url(#clip0_3841_625)">
                <path d="M15.8333 0.833374L14.7833 3.12504L12.5 4.16671L14.7833 5.21671L15.8333 7.50004L16.875 5.21671L19.1666 4.16671L16.875 3.12504M7.49998 3.33337L5.41665 7.91671L0.833313 10L5.41665 12.0834L7.49998 16.6667L9.58331 12.0834L14.1666 10L9.58331 7.91671M15.8333 12.5L14.7833 14.7834L12.5 15.8334L14.7833 16.875L15.8333 19.1667L16.875 16.875L19.1666 15.8334L16.875 14.7834" fill="#003849"/>
              </g>
              <defs>
                <clipPath id="clip0_3841_625">
                  <rect width="20" height="20" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </span>
        </h2>
        <button type="button" className="btn-close" aria-label="Close" onClick={toggle} />
      </div>
      <ModalBody>
        <div className='gm-ia-summary-container'>
          {!loading && <p className='ia-modal-header'>Resumen Generado por GrowMe IA</p>}
          {renderContent()}
        </div>
      </ModalBody>
      <ModalFooter className="action-buttons">
        <div className="generated-by-container">
          <span className="label">Generado por</span>
          <div className="ia-badge">
            <span className="ia-icon">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.5091-2.6067-1.4997z" fill="currentColor"/>
              </svg>
            </span>
            <span>Open AI - GPT 4o</span>
          </div>
        </div>
        <div className="action-buttons-container">
          <button onClick={handleCopy} className="btn btn-outline-secondary" disabled={loading || error || generatingPdf}>
            <HiOutlineClipboardCopy />
            Copiar
          </button>
          <button onClick={handleDownload} className="btn btn-primary" disabled={loading || error || generatingPdf}>
            {generatingPdf ? (
              <>
                <span className="loading-spinner-small"></span> Generando PDF...
              </>
            ) : (
              <>
                <HiOutlineCloudArrowDown />
                Descargar
              </>
            )}
          </button>
        </div>
      </ModalFooter>
      <style jsx>{`
        .loading-spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 1s ease-in-out infinite;
          margin-right: 8px;
          vertical-align: middle;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};

export default IaSummaryModal;

